import * as React from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useGesture } from '@use-gesture/react';

const initialModelPosition = new THREE.Vector3();
const mousePosition2D = new THREE.Vector2();
const mousePosition3D = new THREE.Vector3();
const dragOffset = new THREE.Vector3();
const dragPlaneNormal = new THREE.Vector3();
const dragPlane = new THREE.Plane();

export type DragControlsProps = {
  /** Are drag controls enabled */
  enabled?: boolean;
  /** Lock the drag to a specific axis */
  axisLock?: 'x' | 'y' | 'z';
  /** Drag start event */
  onDragStart?: (origin: THREE.Vector3) => void;
  /** Drag event */
  onDrag?: (localMatrix: THREE.Matrix4) => void;
  /** Drag end event */
  onDragEnd?: () => void;
  /** Object to drag */
  children: React.ReactNode;
};

export const DragControls = React.forwardRef(
  ({ enabled = true, axisLock, onDragStart, onDrag, onDragEnd, children }: DragControlsProps, fRef) => {
    const { camera, size, raycaster, invalidate } = useThree();
    const ref = React.useRef<THREE.Group>(null!);

    const bind = useGesture(
      {
        onDragStart: ({ event }) => {
          event.stopPropagation();

          ref.current.matrix.decompose(initialModelPosition, new THREE.Quaternion(), new THREE.Vector3());
          const { point } = event as any; // oxlint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
          mousePosition3D.copy(point); // oxlint-disable-line @typescript-eslint/no-unsafe-argument
          dragOffset.copy(mousePosition3D).sub(initialModelPosition);

          if (onDragStart) {
            onDragStart(initialModelPosition);
          }
          invalidate();
        },

        onDrag: ({ xy: [dragX, dragY], intentional, tap, event }) => {
          event.stopPropagation();

          if (!intentional || tap) {
            return;
          }

          const normalizedMouseX = ((dragX - size.left) / size.width) * 2 - 1;
          const normalizedMouseY = -((dragY - size.top) / size.height) * 2 + 1;

          mousePosition2D.set(normalizedMouseX, normalizedMouseY);
          raycaster.setFromCamera(mousePosition2D, camera);

          if (!axisLock) {
            camera.getWorldDirection(dragPlaneNormal).negate();
          } else {
            switch (axisLock) {
              case 'x':
                dragPlaneNormal.set(1, 0, 0);
                break;
              case 'y':
                dragPlaneNormal.set(0, 1, 0);
                break;
              case 'z':
                dragPlaneNormal.set(0, 0, 1);
                break;
            }
          }

          dragPlane.setFromNormalAndCoplanarPoint(dragPlaneNormal, mousePosition3D);
          raycaster.ray.intersectPlane(dragPlane, mousePosition3D);

          ref.current.matrix.setPosition(
            mousePosition3D.x - dragOffset.x,
            mousePosition3D.y - dragOffset.y,
            mousePosition3D.z - dragOffset.z
          );

          if (onDrag) {
            onDrag(ref.current.matrix);
          }
          invalidate();
        },

        onDragEnd: ({ event }) => {
          event.stopPropagation();

          ref.current.updateMatrixWorld(true);
          if (onDragEnd) {
            onDragEnd();
          }
          invalidate();
        },
      },
      {
        drag: {
          filterTaps: true,
          threshold: 0,
        },
        enabled,
      }
    );

    React.useImperativeHandle(fRef, () => ref.current, []);

    return (
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      <group ref={ref} {...(bind() as any)} matrixAutoUpdate={false}>
        {children}
      </group>
    );
  }
);

DragControls.displayName = 'DragControls';
